import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft, Phone, Share2, Trash2, MapPin, Home, Wallet,
  Megaphone, User as UserIcon, PhoneCall, ArrowLeftRight,
  Calendar as CalendarIcon, Clock, Flame, Sun, Snowflake, MessageCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import { STATUS_META, STATUS_ORDER, formatDuration } from "@/lib/constants";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import OpportunityDialog from "@/components/OpportunityDialog";
import { LeadTypeBadge } from "@/components/Badges";

const TEMPS = [
  { key: "hot",  label: "Hot",  Icon: Flame,     activeCls: "border-rose-500 bg-rose-50/60 text-rose-700" },
  { key: "warm", label: "Warm", Icon: Sun,       activeCls: "border-amber-400 bg-amber-50/60 text-amber-700" },
  { key: "cold", label: "Cold", Icon: Snowflake, activeCls: "border-sky-500 bg-sky-50/60 text-sky-700" },
];

function initialOf(name = "") {
  const p = name.trim().split(/\s+/);
  return (p[0]?.[0] || "?").toUpperCase();
}

const fmtDT = (iso) =>
  iso ? new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";

export default function LeadDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();

  const leadQ = useQuery({
    queryKey: ["lead", id],
    queryFn: async () => (await api.get(`/leads/${id}`)).data,
    staleTime: 30_000,
  });
  const usersQ = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
    enabled: user?.role === "admin",
    staleTime: 5 * 60_000,
  });
  const actsQ = useQuery({
    queryKey: ["activities", id],
    queryFn: async () => (await api.get(`/leads/${id}/activities`)).data.items,
    enabled: Boolean(id),
    staleTime: 30_000,
  });

  const lead = leadQ.data;
  const [temp, setTemp] = useState("cold");
  const [status, setStatus] = useState("new");
  const [feedback, setFeedback] = useState("");
  const [followDate, setFollowDate] = useState(null);
  const [followTime, setFollowTime] = useState("");
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTo, setTransferTo] = useState("");
  const [oppOpen, setOppOpen] = useState(false);

  useEffect(() => {
    if (lead) {
      setTemp(lead.temperature || "cold");
      setStatus(lead.status || "new");
      setFeedback("");
      if (lead.follow_up_at) {
        const d = new Date(lead.follow_up_at);
        setFollowDate(d);
        setFollowTime(`${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`);
      } else {
        setFollowDate(null);
        setFollowTime("");
      }
    }
  }, [lead?.id]);

  const share = async () => {
    if (!lead) return;
    const url = `${window.location.origin}/leads/${id}`;
    const text = `${lead.name} · ${lead.phone}\n${url}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: lead.name, text, url });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return; // user dismissed share sheet
        // fall through to clipboard fallback
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied — paste into WhatsApp");
    } catch (err) {
      console.error("share fallback failed", err);
      toast.error("Could not copy link");
    }
  };

  const wa = () => {
    const p = String(lead.phone).replace(/\D/g, "");
    window.open(`https://wa.me/${p.startsWith("91") ? p : "91" + p}`, "_blank");
  };
  const call = () => { window.location.href = `tel:${String(lead.phone).replace(/\D/g, "")}`; };

  const saveMut = useMutation({
    mutationFn: async () => {
      const patch = { temperature: temp, status };
      if (followDate) {
        const [hh, mm] = (followTime || "10:00").split(":").map((n) => parseInt(n, 10) || 0);
        const d = new Date(followDate);
        d.setHours(hh, mm, 0, 0);
        patch.follow_up_at = d.toISOString();
      }
      // Fire the two writes in parallel — no reason to serialize them.
      const trimmedFeedback = feedback.trim();
      const calls = [api.patch(`/leads/${id}`, patch)];
      if (trimmedFeedback) {
        calls.push(api.post(`/leads/${id}/activities`, { type: "call", note: trimmedFeedback }));
      }
      const [leadRes, actRes] = await Promise.all(calls);
      return { lead: leadRes?.data, activity: actRes?.data, hadFeedback: Boolean(trimmedFeedback) };
    },
    onSuccess: ({ lead: freshLead, activity: newActivity, hadFeedback }) => {
      toast.success("Feedback saved");
      // Optimistic cache updates — skip network round-trips.
      if (freshLead) qc.setQueryData(["lead", id], freshLead);
      if (hadFeedback && newActivity) {
        qc.setQueryData(["activities", id], (old) =>
          Array.isArray(old) ? [newActivity, ...old] : [newActivity]
        );
      }
      // Cheaper background refresh of the list views — no await.
      qc.invalidateQueries({ queryKey: ["leads"], refetchType: "none" });
      qc.invalidateQueries({ queryKey: ["dashboard"], refetchType: "none" });
      setFeedback("");
    },
    onError: () => toast.error("Save failed"),
  });

  const transferMut = useMutation({
    mutationFn: async () => api.patch(`/leads/${id}`, { assigned_to: transferTo }),
    onSuccess: () => {
      toast.success("Lead transferred");
      qc.invalidateQueries({ queryKey: ["lead", id] });
      qc.invalidateQueries({ queryKey: ["activities", id] });
      qc.invalidateQueries({ queryKey: ["leads"] });
      setTransferOpen(false);
      setTransferTo("");
    },
    onError: () => toast.error("Transfer failed"),
  });

  const typeMut = useMutation({
    mutationFn: async (lead_type) => (await api.patch(`/leads/${id}`, { lead_type })).data,
    onSuccess: (fresh) => {
      qc.setQueryData(["lead", id], fresh);
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(`Moved to ${fresh.lead_type === "live" ? "Live" : "Bulk"} leads`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not change lead type"),
  });

  const delMut = useMutation({
    mutationFn: async () => api.delete(`/leads/${id}`),
    onSuccess: () => { toast.success("Lead deleted"); nav("/leads"); qc.invalidateQueries({ queryKey: ["leads"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const callCount = useMemo(
    () => (actsQ.data || []).filter((a) => a.type === "call").length,
    [actsQ.data]
  );

  if (leadQ.isLoading) return <div className="grid min-h-[300px] place-items-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-[hsl(var(--brand-navy))]" /></div>;
  if (!lead) return <div className="soft-card p-8 text-center" data-testid="lead-detail-not-found"><div className="text-lg font-bold text-navy">Lead not found</div><Link to="/leads" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-navy hover:underline"><ArrowLeft className="h-4 w-4" /> Back</Link></div>;

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="lead-detail-page">
      {/* Custom sticky header */}
      <div className="sticky top-14 z-10 -mx-4 flex items-center justify-between border-b border-slate-100 bg-background/95 px-4 py-3 backdrop-blur lg:top-16">
        <button data-testid="lead-back-btn" onClick={() => nav(-1)} className="grid h-9 w-9 place-items-center rounded-full hover:bg-slate-100" aria-label="Back">
          <ArrowLeft className="h-5 w-5 text-slate-600" />
        </button>
        <h1 className="text-lg font-bold text-navy">Lead Details</h1>
        <div className="flex items-center gap-1">
          <button data-testid="lead-share-btn" onClick={share} className="grid h-9 w-9 place-items-center rounded-full hover:bg-slate-100" aria-label="Share">
            <Share2 className="h-5 w-5 text-navy" />
          </button>
          {user?.role === "admin" && (
            <button data-testid="lead-delete-btn" onClick={() => { if (window.confirm(`Delete ${lead.name}?`)) delMut.mutate(); }} className="grid h-9 w-9 place-items-center rounded-full hover:bg-rose-50" aria-label="Delete">
              <Trash2 className="h-5 w-5 text-rose-500" />
            </button>
          )}
        </div>
      </div>

      {/* Hero */}
      <section className="soft-card p-6 text-center" data-testid="lead-hero">
        <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-[#F0EAD9] text-3xl font-bold text-[hsl(var(--brand-gold))]">
          {initialOf(lead.name)}
        </div>
        <h2 className="mt-3 text-2xl font-bold text-navy">{lead.name}</h2>
        <div className="text-sm text-slate-500">{lead.phone}</div>
        <div className="mt-2 flex flex-col items-center gap-1.5">
          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium badge-${lead.status}`}>
            {STATUS_META[lead.status]?.label}
          </span>
          <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium badge-${lead.temperature}`}>
            <Snowflake className="h-3 w-3" /> {lead.temperature.charAt(0).toUpperCase() + lead.temperature.slice(1)} Lead
          </span>
        </div>
        <div className="mt-5 flex gap-3 justify-center">
          <Button data-testid="hero-call-btn" onClick={call} className="h-12 rounded-full bg-gold px-6 text-base font-semibold text-white hover:brightness-110">
            <Phone className="h-4 w-4" /> Call Now
          </Button>
          <Button data-testid="hero-wa-btn" onClick={wa} className="h-12 rounded-full bg-emerald-500 px-6 text-base font-semibold text-white hover:bg-emerald-600">
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </Button>
        </div>
      </section>

      {/* Facts list */}
      <section className="soft-card divide-y divide-slate-100 px-5" data-testid="lead-facts">
        <FactRow Icon={MapPin} label="Location" value={lead.location || "—"} />
        <FactRow Icon={Home} label="Property Type" value={lead.project || "—"} />
        <FactRow Icon={Wallet} label="Budget" value={lead.budget || "—"} />
        <FactRow Icon={Megaphone} label="Source" value={lead.source || "—"} />
        <FactRow Icon={UserIcon} label="Assigned To" value={lead.assigned_to_name || <span className="italic text-slate-400">Unassigned</span>} />
        <FactRow Icon={PhoneCall} label="Calls" value={callCount || "—"} />
      </section>

      {/* Transfer button (admin only) */}
      {user?.role === "admin" && (
      <Popover open={transferOpen} onOpenChange={setTransferOpen}>
        <PopoverTrigger asChild>
          <button data-testid="transfer-btn" className="soft-card flex w-full items-center justify-center gap-2 py-3 text-sm font-semibold text-navy hover:bg-slate-50">
            <ArrowLeftRight className="h-4 w-4" /> Transfer to Sales
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3">
          <div className="mb-2 text-xs font-semibold text-slate-600">Transfer this lead to…</div>
          <Select value={transferTo} onValueChange={setTransferTo}>
            <SelectTrigger data-testid="transfer-select"><SelectValue placeholder="Choose teammate" /></SelectTrigger>
            <SelectContent>
              {(usersQ.data || [])
                .filter((u) => u.id !== lead.assigned_to && !u.is_off)
                .map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
            </SelectContent>
          </Select>
          <Button data-testid="transfer-confirm" className="mt-2 w-full bg-navy" disabled={!transferTo || transferMut.isPending} onClick={() => transferMut.mutate()}>
            Confirm transfer
          </Button>
        </PopoverContent>
      </Popover>
      )}

      {/* Opportunity + Bulk/Live tag */}
      <section className="soft-card flex flex-wrap items-center justify-between gap-3 p-4" data-testid="lead-opp-section">
        <div className="flex items-center gap-2">
          <LeadTypeBadge type={lead.lead_type} />
          {user?.role === "admin" && (
            <div className="flex rounded-full bg-slate-100 p-0.5 text-[11px] font-semibold" data-testid="lead-type-toggle">
              {["bulk", "live"].map((t) => (
                <button
                  key={t}
                  data-testid={`lead-type-set-${t}`}
                  disabled={typeMut.isPending}
                  onClick={() => (lead.lead_type || "bulk") !== t && typeMut.mutate(t)}
                  className={`rounded-full px-3 py-1 capitalize transition-colors ${
                    (lead.lead_type || "bulk") === t ? "bg-white text-navy shadow-sm" : "text-slate-500"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          )}
        </div>
        {lead.opportunity_id ? (
          <Link
            to="/opportunities"
            data-testid="lead-detail-opp-created"
            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200"
          >
            Opportunity Created →
          </Link>
        ) : (
          <Button data-testid="lead-detail-create-opp-btn" onClick={() => setOppOpen(true)} className="rounded-full bg-navy hover:brightness-110">
            Create Opportunity
          </Button>
        )}
      </section>
      <OpportunityDialog open={oppOpen} onOpenChange={setOppOpen} lead={lead} />

      {/* Update Status & Log Feedback */}
      <h3 className="pt-2 text-base font-bold text-navy" data-testid="feedback-heading">Update Status &amp; Log Feedback</h3>

      {/* Temperature */}
      <div>
        <div className="mb-2 text-sm font-semibold text-slate-700">Lead Temperature</div>
        <div className="grid grid-cols-3 gap-2">
          {TEMPS.map((t) => {
            const active = temp === t.key;
            return (
              <button
                key={t.key}
                data-testid={`temp-${t.key}`}
                data-active={active}
                onClick={() => setTemp(t.key)}
                className={`flex items-center justify-center gap-2 rounded-2xl border-2 py-3 text-sm font-semibold transition ${active ? t.activeCls : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
              >
                <t.Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Status */}
      <div>
        <div className="mb-2 text-sm font-semibold text-slate-700">Status</div>
        <div className="flex flex-wrap gap-2">
          {STATUS_ORDER.map((s) => (
            <button
              key={s}
              data-testid={`status-${s}`}
              data-active={status === s}
              onClick={() => setStatus(s)}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition ${status === s ? "border-[hsl(var(--brand-navy))] bg-[hsl(var(--brand-navy))] text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
            >
              {STATUS_META[s].label}
            </button>
          ))}
        </div>
      </div>

      {/* Feedback textarea */}
      <Textarea
        data-testid="feedback-input"
        rows={4}
        placeholder="Log call feedback here..."
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        className="rounded-2xl border-slate-200 bg-white"
      />

      {/* Follow-up date & time */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[hsl(var(--brand-gold))]"><Clock className="h-4 w-4" /></span>
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="pick-date" className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                <CalendarIcon className="h-4 w-4 text-slate-500" />
                {followDate ? followDate.toLocaleDateString() : "Pick date"}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0">
              <Calendar mode="single" selected={followDate} onSelect={setFollowDate} initialFocus />
            </PopoverContent>
          </Popover>
          <label className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-sm font-semibold text-slate-700">
            <Clock className="h-4 w-4 text-slate-500" />
            <input
              data-testid="pick-time"
              type="time"
              value={followTime}
              onChange={(e) => setFollowTime(e.target.value)}
              className="bg-transparent outline-none"
            />
          </label>
          {followDate && (
            <button data-testid="clear-followup" onClick={() => { setFollowDate(null); setFollowTime(""); }} className="text-xs text-slate-500 hover:text-slate-700 underline">
              Clear
            </button>
          )}
        </div>
        <p className="text-xs text-slate-500">
          Set a follow-up date/time to get a &quot;Call this lead&quot; notification 5 minutes before.
        </p>
      </div>

      <Button
        data-testid="save-feedback-btn"
        className="h-12 w-full rounded-2xl bg-navy text-base font-semibold hover:brightness-110 disabled:bg-slate-400 disabled:text-white"
        disabled={saveMut.isPending}
        onClick={() => saveMut.mutate()}
      >
        {saveMut.isPending ? "Saving…" : "Save Feedback"}
      </Button>

      {/* Activity History */}
      <h3 className="pt-3 text-base font-bold text-navy">Activity History</h3>
      <div className="space-y-2 pb-6">
        {(actsQ.data || []).length === 0 && (
          <div className="soft-card p-6 text-center text-sm text-slate-400">No activity yet.</div>
        )}
        {(actsQ.data || []).map((a) => (
          <div key={a.id} className="soft-card p-4" data-testid={`activity-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-bold text-navy">{a.user_name}</div>
                <div className="mt-0.5 text-sm text-slate-700">
                  {a.note || (a.status_to ? `Status → ${STATUS_META[a.status_to]?.label || a.status_to}` : "—")}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  {new Date(a.created_at).toLocaleString()}
                  {a.duration_seconds ? ` · ${formatDuration(a.duration_seconds)}` : ""}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                {lead.temperature && (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium badge-${lead.temperature}`}>
                    <Snowflake className="h-2.5 w-2.5" /> {lead.temperature[0].toUpperCase() + lead.temperature.slice(1)}
                  </span>
                )}
                {a.status_to && (
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium badge-${a.status_to}`}>
                    {STATUS_META[a.status_to]?.label}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {lead.follow_up_at && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 rounded-full bg-amber-100 px-4 py-2 text-xs font-semibold text-amber-800 shadow-lg lg:bottom-6" data-testid="followup-banner">
          <CalendarIcon className="mr-1 inline h-3 w-3" /> Follow-up scheduled · {fmtDT(lead.follow_up_at)}
        </div>
      )}
    </div>
  );
}

function FactRow({ Icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-3.5">
      <div className="flex items-center gap-3 text-sm text-slate-600">
        <Icon className="h-4 w-4 text-slate-400" />
        {label}
      </div>
      <div className="text-sm font-semibold text-navy text-right">{value}</div>
    </div>
  );
}
